import { DotsThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { MemberRole } from "~/api/governanceTypes";
import {
  useActivateMember,
  useRemoveMember,
  useResendInvite,
  useSetMemberRole,
  useSuspendMember,
  useViewer,
} from "~/api/queries";
import type { Member } from "~/api/types";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";

const ROLES: MemberRole[] = ["member", "viewer", "guest", "admin"];

function roleLabel(role: MemberRole, t: (key: string) => string): string {
  switch (role) {
    case "viewer":
      return t("Viewer");
    case "guest":
      return t("Guest");
    case "admin":
      return t("Admin");
    default:
      return t("Member");
  }
}

/**
 * Clean-room (§A.6.1), derived only from `UsersController.Actions.cs`: the server refuses
 * self-demotion with `"Cannot change your own role."` — disabled here on your own row rather
 * than discovered after a refused request.
 */
export function MemberRowMenu({ member }: { member: Member }) {
  const { t } = useTranslation();
  const { data: viewer } = useViewer();
  const setRole = useSetMemberRole();
  const suspend = useSuspendMember();
  const activate = useActivateMember();
  const resendInvite = useResendInvite();
  const removeMember = useRemoveMember();
  const [confirmRemove, setConfirmRemove] = useState(false);

  const isSelf = viewer?.id === member.id;
  const neverSignedIn = !member.lastActiveAt;

  const items: MenuItem[] = [
    {
      type: "submenu",
      title: t("Change role"),
      items: ROLES.map((role) => ({
        type: "button",
        title: roleLabel(role, t),
        selected: member.role === role,
        disabled: isSelf || member.role === role,
        onClick: () =>
          setRole.mutate(
            { id: member.id, role },
            { onSuccess: () => toast.success(t("Role updated")) },
          ),
      })),
    },
    neverSignedIn
      ? {
          type: "button",
          title: t("Resend invite"),
          onClick: () =>
            resendInvite.mutate(member.id, { onSuccess: () => toast.success(t("Invite resent")) }),
        }
      : {
          type: "button",
          title: member.suspended ? t("Activate") : t("Suspend"),
          disabled: isSelf,
          onClick: () =>
            member.suspended
              ? activate.mutate(member.id, {
                  onSuccess: () => toast.success(t("Member activated")),
                })
              : suspend.mutate(member.id, {
                  onSuccess: () => toast.success(t("Member suspended")),
                }),
        },
    { type: "separator" },
    {
      type: "button",
      title: t("Remove…"),
      dangerous: true,
      disabled: isSelf,
      onClick: () => setConfirmRemove(true),
    },
  ];

  return (
    <>
      <Menu
        items={items}
        trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} size="sm" />}
      />
      <ConfirmDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t("Remove {{name}}?", { name: member.name || member.email })}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={removeMember.isPending}
        onConfirm={() =>
          removeMember.mutate(member.id, {
            onSuccess: () => {
              setConfirmRemove(false);
              toast.success(t("Member removed"));
            },
          })
        }
      />
    </>
  );
}
